import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { loadConfig } from './config.js';
import { waitForHandshake, type VarioHandshake } from './vario-handshake.js';
import { exchangeAppToken, AuthBridgeFailure, type AuthBridgeSession } from './auth-bridge-client.js';import { renderInstall } from './views/install.js';
import { renderDashboard } from './views/dashboard.js';
import { renderUninstall } from './views/uninstall.js';
import { renderOrdersPage } from './views/orders-page.js';
import { renderProductsPage } from './views/products-page.js';
import { renderHelpPage } from './views/help-page.js';
import { renderSettingsPage } from './views/settings-page.js';
import { renderLogPage } from './views/log-page.js';
import { renderListToGambioPage } from './views/list-to-gambio-page.js';
import { renderListingStatus } from './views/listing-status.js';
import { renderAccountCustomerExport } from './views/account-customer-export.js';
import { initVarioIframeHeight, requestVarioFullHeight, signalVarioReady } from './vario-iframe.js';

// Bridge-Layer aus dem geteilten Design-Package - MUSS als ALLERLETZTER CSS-Import
// laden (Specificity gegen Legacy-Regeln). Identisch zu frontend-vario-kaufland.
import '@vario-connect/frontend-design/bridge.css';
import { initConnectionPills } from './lib/connection-pill.js';

const trace = (...args: unknown[]) => console.log('[gambio-app]', ...args);
const traceErr = (...args: unknown[]) => console.error('[gambio-app]', ...args);

type Slot = 'install' | 'uninstall' | 'dashboard' | 'settings' | 'orders' | 'products' | 'list-to-gambio' | 'listing-status' | 'account-customer-export' | 'log' | 'help';

function readSlot(): Slot {
  const hash = window.location.hash.replace(/^#\/?/, '').toLowerCase();
  if (hash === 'install' || hash === 'uninstall' || hash === 'settings' || hash === 'orders' || hash === 'products' || hash === 'list-to-gambio' || hash === 'listing-status' || hash === 'account-customer-export' || hash === 'log' || hash === 'help') {
    return hash;
  }
  return 'dashboard';
}

/** Direkt im Browser geöffnet: kein Token in der URL und kein umgebendes VARIO-iframe. */
function isOutsideVario(): boolean {
  if (new URLSearchParams(window.location.search).get('appToken')) return false;
  if (new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token')) return false;
  return window.parent === window;
}

async function bootstrap() {
  const outsideVario = isOutsideVario();

  // Listing-Slot laeuft im `auto`-Modus, damit grosse States (Lookup, Bilder-Liste,
  // Decide-Vorschlaege) ihren Platz bekommen - die Mindesthoehe im Helper
  // sorgt dafuer, dass die kompakte "schon im Lister"-Ready-Box trotzdem
  // bedienbar bleibt.
  if (!outsideVario) {
    // Der Verkaufskanal-Slot /#/orders überlässt Vario das Höhen-Handling (volle Viewport-Höhe via
    // useFullHeight) und legt sein Layout auf 100% aus + scrollt intern (orders-page.ts) - stabiler
    // als das Content-Höhen-Melden. Alle anderen Slots bleiben beim fixen Korridor.
    const earlySlot = readSlot();
    if (earlySlot === 'orders' || earlySlot === 'products' || earlySlot === 'list-to-gambio' || earlySlot === 'settings' || earlySlot === 'log' || earlySlot === 'help') {
      requestVarioFullHeight();
    } else if (earlySlot === 'listing-status' || earlySlot === 'account-customer-export') {
      // Inline-Detail-Slots (z. B. products.articles.detail.tabs.listings.detail.{appId},
      // accounts.detail.tabs.listings.detail.{appId}) - KEIN Vollflächen-Tab. useFullHeight
      // wird von Vario hier nicht honoriert → iframe kollabiert auf 0 ("Feld da, aber leer").
      // Stattdessen Content-Höhe melden (auto), wie Ottos otto-listing-status.
      initVarioIframeHeight('auto');
    } else if (earlySlot === 'install' || earlySlot === 'uninstall') {
      // Install-/Uninstall-Dialog: iframe = Inhaltshöhe (Karte), Vario scrollt außen und setzt
      // seinen OK-Button direkt unter die Karte. Der fixe 900px-Korridor liesse die Karte oben
      // kleben mit viel Leerraum darunter (Uninstall: kurze Meldung, OK „1,5 Seiten" tiefer).
      // renderInstall/renderUninstall setzen dazu html.gambio-autogrow (löst die bridge-§16-
      // 100%-Kette). Identisch zum Otto-Install-/Uninstall-Slot.
      initVarioIframeHeight('auto');
    } else {
      initVarioIframeHeight('fixed');
    }
  }

  trace('bootstrap start', {
    href: window.location.href,
    origin: window.location.origin,
    outsideVario,
    isInIframe: window.parent !== window,
    parentOrigin: (() => {
      try { return window.parent !== window ? document.referrer : '(top)'; } catch { return '(crossorigin)'; }
    })(),
  });

  const statusEl = document.querySelector<HTMLParagraphElement>('#status')!;
  const dotEl = document.querySelector<HTMLSpanElement>('#status-dot')!;
  const contentEl = document.querySelector<HTMLElement>('#content')!;

  const setStatus = (text: string, kind: 'info' | 'ok' | 'error' = 'info') => {
    dotEl.className = `status-dot status-dot--${kind}`;
    dotEl.title = text;
    if (kind === 'error') {
      statusEl.textContent = text;
      statusEl.className = 'error';
      statusEl.hidden = false;
    } else {
      statusEl.hidden = true;
      statusEl.textContent = '';
    }
    trace('status:', text);
  };

  let config;
  try {
    config = loadConfig();
  } catch (e) {
    setStatus(`Konfiguration unvollständig: ${(e as Error).message}`, 'error');
    return;
  }

  // Die App läuft nur im VARIO-iframe. Direkt im Browser geöffnet gibt es keinen Token, also
  // gleich Bescheid geben statt 15 Sekunden auf den Handshake zu warten.
  if (outsideVario) {
    setStatus('Diese App läuft nur innerhalb von VARIO. Bitte über VARIO öffnen.', 'error');
    return;
  }

  let session: AuthBridgeSession;
  let handshake: VarioHandshake;

  setStatus('Warte auf Vario-Handshake …');
  try {
    handshake = await waitForHandshake(config.allowedHostOrigins);
  } catch (e) {
    setStatus(`Handshake fehlgeschlagen: ${(e as Error).message}`, 'error');
    return;
  }

  setStatus('Tausche Token gegen Supabase-Session …');
  try {
    session = await exchangeAppToken(config.authBridgeUrl, handshake.appToken);
  } catch (e) {
    if (e instanceof AuthBridgeFailure) {
      setStatus(`Auth-Bridge ${e.status}: ${e.code} - ${e.message}`, 'error');
    } else {
      setStatus(`Auth-Bridge unerreichbar: ${(e as Error).message}`, 'error');
    }
    return;
  }

  const supabase: SupabaseClient = createClient(config.supabaseUrl, config.supabaseAnonKey, {
    global: { headers: { Authorization: `Bearer ${session.token}` } },
    auth: { persistSession: false, autoRefreshToken: false },
  });

  setStatus(`Verbunden - Tenant ${session.tenantId.slice(0, 8)}…`, 'ok');
  // Verbindungs-Pillen wissen ab jetzt, wohin sie ihre Messung schicken (s. lib/connection-pill.ts).
  initConnectionPills({ supabaseUrl: config.supabaseUrl, token: session.token });

  const slot = readSlot();
  trace('routing to slot', slot);

  // Token-Recovery: Vario liefert den offlineToken im Install-Slot bzw. beim „Offline Token
  // erneuern" aus dem App-Store. Geht der tenant-weite Token mal verloren (Shop-Löschung vor
  // Migration 0140), greifen wir ihn auf JEDEM Slot ab, sobald er in der URL steht - auch im
  // Install-Slot, damit „Offline Token erneuern" sofort persistiert, ohne dass das Install-
  // Formular ausgefüllt werden muss. Idempotent + non-destruktiv: gambio_set_offline_token
  // hängt ihn an einen bestehenden Shop (gibt es keinen, übernimmt der reguläre Install-Flow).
  if (handshake.offlineToken) {
    trace('offline token present on slot', slot, '→ persisting via gambio_set_offline_token');
    try {
      const { data, error } = await supabase.rpc('gambio_set_offline_token', {
        _token: handshake.offlineToken,
      });
      if (error) traceErr('offline token capture failed', error.message);
      else trace('offline token capture result', data);
    } catch (e) {
      traceErr('offline token capture exception', (e as Error).message);
    }
  } else {
    trace('no offlineToken on slot', slot);
  }

  const ctx = {
    contentEl,
    supabase,
    session: {
      token: session.token,
      tenantId: session.tenantId,
      marketplaceKind: session.marketplaceKind,
    },
    handshake,
    supabaseUrl: config.supabaseUrl,
  };

  // Verbindungsanzeige wie im Otto-Modul: die einzelnen Ansichten (Verkaufskanal-Tabs + Artikel-
  // Slot) bringen ihre eigene „● Gambio verbunden"-Kennung im Kopf mit. Der reine Boot-/Handshake-
  // Punkt #status-dot (index.html, oben rechts) wird in diesen Ansichten ausgeblendet - sonst
  // stünde ein kontextloser Punkt neben der Kennung. install/uninstall behalten ihn, weil sie dort
  // Fortschritt/Fehler über setStatus anzeigen.
  const SLOTS_WITH_OWN_CHROME: Slot[] = [
    'dashboard', 'orders', 'products', 'list-to-gambio', 'listing-status', 'account-customer-export', 'settings', 'log', 'help',
  ];
  if (SLOTS_WITH_OWN_CHROME.includes(slot)) {
    const statusDotEl = document.querySelector<HTMLElement>('#status-dot');
    if (statusDotEl) statusDotEl.style.display = 'none';
  }

  switch (slot) {
    case 'install':
      await renderInstall(ctx);
      break;
    case 'dashboard':
      await renderDashboard(ctx);
      break;
    case 'uninstall':
      await renderUninstall(ctx);
      break;
    case 'orders':
      await renderOrdersPage(ctx);
      break;
    case 'products':
      await renderProductsPage(ctx);
      break;
    case 'list-to-gambio':
      await renderListToGambioPage(ctx);
      break;
    case 'listing-status':
      await renderListingStatus(ctx);
      break;
    case 'account-customer-export':
      await renderAccountCustomerExport(ctx);
      break;
    case 'settings':
      await renderSettingsPage(ctx);
      break;
    case 'log':
      await renderLogPage(ctx);
      break;
    case 'help':
      renderHelpPage(ctx);
      break;
  }

  // VARIO-Ready-Signal: App vollständig geladen und im Vario-iframe erreichbar
  // (Host wertet ab 2026-08-20 aus). Nur im Erfolgsfall; Fehlerpfade
  // erreichen diese Stelle nicht.
  signalVarioReady();
}

bootstrap().catch((err) => {
  traceErr('bootstrap fatal', err);
  const statusEl = document.querySelector<HTMLParagraphElement>('#status');
  if (statusEl) {
    statusEl.textContent = `Bootstrap-Fehler: ${(err as Error).message ?? err}`;
    statusEl.className = 'error';
  }
});

window.addEventListener('error', (e) => traceErr('window error', e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) => traceErr('unhandled rejection', e.reason));
